import React, { useMemo } from 'react';
import {
  StatisticsSectionContainer,
  formatNumber,
  formatPercent,
  AnalyticsDonut,
  computePeriodDelta,
  getPlanCreditCapacity,
  AnalyticsGlyph,
} from './StatisticsSectionLayout';

export default function StatisticsProvidersSection({
  empresaValues = {},
  metrics = {},
  activeRequests = 0,
  unreadNotifications = 0,
  companyProfileViewTotal = 0,
  companyProfileViewRecent = 0,
  userProfileViewTotal = 0,
  userProfileViewRecent = 0,
  publicationViewTotal = 0,
  publicationViewRecent = 0,
  teamSize = 0,
}) {
  const publications = Number(metrics.publications) || 0;
  const publicationLimit = Number(empresaValues.publicationLimit) || 0;
  const publicationUsagePercent =
    publicationLimit > 0 ? Math.min(100, Math.round((publications / publicationLimit) * 100)) : 0;
  const units = Number(metrics.units) || 0;
  const leads = Math.max(0, Number(empresaValues.leadCredits) || 0);
  const ppcCredits = Math.max(0, Number(empresaValues.ppcCredits) || 0);
  const combinedCredits = leads + ppcCredits;

  const planCapacity = getPlanCreditCapacity(empresaValues.membershipPlan);
  const creditPercent =
    planCapacity > 0 ? Math.min(100, Math.round((combinedCredits / planCapacity) * 100)) : 0;
  const creditTone = creditPercent >= 60 ? 'success' : creditPercent >= 30 ? 'warning' : 'danger';

  const userLimit = Number(empresaValues.userLimit) || 0;
  const userUsagePercent =
    userLimit > 0 ? Math.round(((teamSize || 0) / userLimit) * 100) : teamSize > 0 ? 75 : 0;
  const userTone = userUsagePercent >= 90 ? 'danger' : userUsagePercent >= 70 ? 'warning' : 'success';
  const seatsCaption =
    userLimit > 0
      ? `${Math.max(userLimit - teamSize, 0)} asesores disponibles`
      : 'Define un límite para tu equipo';

  const visitsRecent = publicationViewRecent + companyProfileViewRecent;
  const visitsPrevious = Math.max((publicationViewTotal || 0) - publicationViewRecent, 0);

  const funnelSteps = useMemo(() => {
    const safeVisits = Math.max(visitsRecent, 0);
    const leadsStep = Math.min(leads, safeVisits);
    const fallbackLeadStep = leadsStep > 0 ? leadsStep : Math.min(activeRequests, safeVisits);
    const requestPercent =
      fallbackLeadStep > 0 ? Math.round((activeRequests / fallbackLeadStep) * 100) : 0;

    return [
      {
        id: 'visits',
        label: 'Visitas',
        value: safeVisits,
        percent: 100,
        caption: `${formatNumber(publicationViewRecent)} en fichas`,
      },
      {
        id: 'leads',
        label: 'Leads',
        value: fallbackLeadStep,
        percent: safeVisits > 0 ? Math.round((fallbackLeadStep / safeVisits) * 100) : 0,
        caption: fallbackLeadStep > 0 ? 'Interés capturado' : 'Activa campañas para generar leads',
      },
      {
        id: 'requests',
        label: 'Solicitudes',
        value: activeRequests,
        percent: requestPercent,
        caption: activeRequests > 0 ? 'Seguimiento comercial' : 'Sin solicitudes abiertas',
      },
    ];
  }, [activeRequests, leads, publicationViewRecent, visitsRecent]);

  const heroCards = useMemo(
    () => [
      {
        id: 'visits',
        label: 'Visitas a publicaciones',
        value: formatNumber(publicationViewRecent),
        delta: computePeriodDelta(publicationViewRecent, visitsPrevious),
        caption: `${formatNumber(companyProfileViewRecent)} visitas al perfil`,
        icon: <AnalyticsGlyph variant="radar" />,
        tone: 'positive',
      },
      {
        id: 'requests',
        label: 'Solicitudes activas',
        value: formatNumber(activeRequests),
        caption:
          activeRequests > 0 ? 'Oportunidades listas para seguimiento' : 'Sin solicitudes abiertas',
        icon: <AnalyticsGlyph variant="target" />,
        tone: activeRequests > 0 ? 'warning' : 'neutral',
        delta: {
          direction: activeRequests > 0 ? 'warning' : 'neutral',
          value: activeRequests > 0 ? 'Activo' : 'Sin actividad',
          caption: activeRequests > 0 ? 'Seguimiento pendiente' : 'Sin solicitudes',
        },
      },
      {
        id: 'publications',
        label: 'Publicaciones activas',
        value: formatNumber(publications),
        delta:
          publicationLimit > 0
            ? {
                direction: publicationUsagePercent >= 85 ? 'negative' : 'positive',
                value: `${100 - publicationUsagePercent}% libre`,
                caption: 'Capacidad del plan',
              }
            : null,
        caption:
          publicationLimit > 0
            ? `${formatNumber(publications)} / ${formatNumber(publicationLimit)} posibles`
            : 'Plan sin límite definido',
        icon: <AnalyticsGlyph variant="stack" />,
        tone: publicationUsagePercent >= 85 ? 'danger' : 'neutral',
      },
      {
        id: 'units',
        label: 'Unidades registradas',
        value: formatNumber(units),
        caption: units > 0 ? 'Inventario disponible' : 'Agrega unidades para mejorar tu oferta',
        icon: <AnalyticsGlyph variant="pulse" />,
        tone: units > 0 ? 'positive' : 'neutral',
        delta: units > 0
          ? {
              direction: 'positive',
              value: `${formatNumber(units)} en inventario`,
              caption: 'Listas para asignar',
            }
          : {
              direction: 'neutral',
              value: 'Sin unidades',
              caption: 'Agrega tu flota',
            },
      },
    ],
    [
      activeRequests,
      companyProfileViewRecent,
      publicationLimit,
      publicationUsagePercent,
      publicationViewRecent,
      publications,
      units,
      visitsPrevious,
    ]
  );

  const resourceDonuts = useMemo(
    () => [
      {
        id: 'credits',
        label: 'Créditos disponibles',
        value: formatNumber(combinedCredits),
        caption:
          planCapacity > 0 ? `${formatPercent(creditPercent)} del plan` : 'Sin límite configurado',
        percent: creditPercent,
        tone: creditTone,
      },
      {
        id: 'team',
        label: 'Límite de usuarios',
        value:
          userLimit > 0
            ? `${formatNumber(teamSize)} / ${formatNumber(userLimit)}`
            : formatNumber(teamSize),
        caption: seatsCaption,
        percent: userUsagePercent,
        tone: userTone,
      },
    ],
    [
      creditPercent,
      creditTone,
      combinedCredits,
      planCapacity,
      seatsCaption,
      teamSize,
      userLimit,
      userTone,
      userUsagePercent,
    ]
  );

  const actionCards = useMemo(() => {
    const cards = [
      {
        id: 'optimize-inventory',
        title: 'Optimiza tus fichas',
        body:
          publicationUsagePercent < 80
            ? 'Todavía hay espacio para lanzar más publicaciones estratégicas.'
            : 'Actualiza las fichas top para mantener la conversión.',
        cta: 'Gestionar publicaciones →',
        href: '/dashboard?section=publicaciones',
      },
      {
        id: 'leads',
        title: 'Impulsa los leads',
        body:
          combinedCredits > 0
            ? `Hay ${formatNumber(leads)} leads disponibles para contacto inmediato.`
            : 'Recarga créditos para seguir captando oportunidades calificadas.',
        cta: combinedCredits > 0 ? 'Ver leads →' : 'Recargar ahora →',
        href: combinedCredits > 0 ? '/dashboard?section=promocionar' : '/dashboard?section=suscripciones',
      },
    ];

    cards.push(
      activeRequests > 0
        ? {
            id: 'followup',
            title: 'Prioriza seguimiento',
            body: `Hay ${formatNumber(
              activeRequests
            )} solicitudes abiertas que pueden cerrar esta semana.`,
            cta: 'Ver solicitudes →',
            href: '/dashboard?section=solicitudes',
          }
        : {
            id: 'notifications',
            title: 'Mantén tu SLA',
            body:
              unreadNotifications > 0
                ? `${formatNumber(unreadNotifications)} alertas requieren tu atención.`
                : 'Sin alertas pendientes. Mantén esta respuesta.',
            cta: unreadNotifications > 0 ? 'Revisar alertas →' : 'Configurar alertas →',
            href: '/dashboard?section=notificaciones',
          }
    );

    return cards;
  }, [activeRequests, combinedCredits, leads, publicationUsagePercent, unreadNotifications]);

  return (
    <div className="statistics-page statistics-page--executive">
      <StatisticsSectionContainer
        title="Estadísticas de proveedores"
        subtitle="Consulta los datos reales de tu operación en Flotte para decidir los siguientes pasos comerciales."
        periodLabel="Últimos 30 días"
      >
        <div className="analytics-hero-grid">
          {heroCards.map(card => (
            <article key={card.id} className={`analytics-hero-card analytics-hero-card--${card.tone}`}>
              <div className="analytics-hero-card__heading">
                <span className="analytics-hero-card__icon" aria-hidden="true">
                  {card.icon}
                </span>
                <span className="analytics-hero-card__label">{card.label}</span>
              </div>
              <div className="analytics-hero-card__value statistics-mono">{card.value}</div>
              <div className={`analytics-hero-card__trend is-${card.delta?.direction || 'neutral'}`}>
                <span>{card.delta?.value || '•'}</span>
                <small>{card.delta?.caption || 'Sin comparativo'}</small>
              </div>
              {card.caption ? <p className="analytics-hero-card__caption">{card.caption}</p> : null}
            </article>
          ))}
        </div>

        <div className="analytics-performance">
          <section className="analytics-panel analytics-panel--funnel">
            <header>
              <h3>Funnel de conversión</h3>
              <p>Visibilidad completa del flujo desde visitas hasta solicitudes.</p>
            </header>
            <div className="analytics-funnel">
              {funnelSteps.map(step => (
                <div key={step.id} className="analytics-funnel-step">
                  <div className="analytics-funnel-bar">
                    <span
                      className={`analytics-funnel-bar__fill analytics-funnel-bar__fill--${step.id}`}
                      style={{ width: `${Math.max(10, step.percent)}%` }}
                    />
                  </div>
                  <div className="analytics-funnel-meta">
                    <div>
                      <strong className="statistics-mono">{formatNumber(step.value)}</strong>
                      <span>{step.label}</span>
                    </div>
                    <small>{step.percent}%</small>
                  </div>
                  <p>{step.caption}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="analytics-panel analytics-panel--donuts">
            <header>
              <h3>Recursos y salud</h3>
              <p>Controla créditos, equipo y capacidad comercial en tiempo real.</p>
            </header>
            <div className="analytics-donut-grid">
              {resourceDonuts.map(item => (
                <AnalyticsDonut
                  key={item.id}
                  percent={item.percent}
                  label={item.label}
                  value={item.value}
                  caption={item.caption}
                  tone={item.tone}
                />
              ))}
            </div>
          </section>
        </div>

        <section className="insights-panel">
          <div className="insights-panel__header">
            <div>
              <h3>Insights operativos</h3>
              <p>Prioriza acciones que cierran brechas comerciales.</p>
            </div>
          </div>
          <ul className="insights-list">
            {actionCards.map(card => (
              <li key={card.id} className="insights-list__item">
                <div className="insights-list__text">
                  <strong>{card.title}</strong>
                  <p>{card.body}</p>
                </div>
                <a className="insights-list__action" href={card.href}>
                  {card.cta}
                </a>
              </li>
            ))}
          </ul>
        </section>
      </StatisticsSectionContainer>
    </div>
  );
}
